/**
 * API · core.saved_messages (Fase B·F2)
 *
 * Bookmarks personales (privados por usuario). RLS owner-only, `user_id` tiene
 * DEFAULT auth.uid() → no hace falta mandarlo. Para la vista "Guardados"
 * componemos el mensaje real en una segunda query (evita ambigüedad de embed).
 */
import { supabase } from '@/lib/supabase';
import type { MessagesRow } from '@/types/database.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface SavedMessageRow {
  id: string;
  user_id: string;
  message_id: string;
  created_at: string;
}

export interface SavedMessageWithBody {
  saved_id: string;
  /** message_id del bookmark (para quitarlo aunque el mensaje haya sido borrado). */
  message_id: string;
  saved_at: string;
  message: MessagesRow | null;
}

/** IDs de mensajes guardados por el usuario — para marcar el ícono en la lista. */
export async function listSavedMessageIds(): Promise<string[]> {
  const { data, error } = await db.from('saved_messages').select('message_id');
  if (error) throw error;
  return ((data ?? []) as { message_id: string }[]).map((r) => r.message_id);
}

/** Guardados con el mensaje resuelto, para la vista "Guardados". */
export async function listSavedMessages(): Promise<SavedMessageWithBody[]> {
  const { data, error } = await db
    .from('saved_messages')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as SavedMessageRow[];
  if (rows.length === 0) return [];

  const ids = rows.map((r) => r.message_id);
  const { data: msgs, error: msgErr } = await db
    .from('messages')
    .select('*')
    .in('id', ids);
  if (msgErr) throw msgErr;
  const byId = new Map<string, MessagesRow>(
    ((msgs ?? []) as MessagesRow[]).map((m) => [m.id, m]),
  );
  return rows.map((r) => ({
    saved_id: r.id,
    message_id: r.message_id,
    saved_at: r.created_at,
    message: byId.get(r.message_id) ?? null,
  }));
}

export async function saveMessage(messageId: string): Promise<void> {
  const { error } = await db
    .from('saved_messages')
    .insert({ message_id: messageId });
  if (error && error.code !== '23505') throw error;
}

export async function unsaveMessage(messageId: string): Promise<void> {
  // RLS owner-only → el delete solo alcanza la fila propia.
  const { error } = await db
    .from('saved_messages')
    .delete()
    .eq('message_id', messageId);
  if (error) throw error;
}
